import React from 'react';
import { Vega } from 'react-vega';
import styled from 'styled-components';
import { transformDataForVega, extractFields, ChartConfig } from '../utils/chartDataTransformer';

const ChartContainer = styled.div<{ chartType?: string }>`
  // background: rgba(255, 255, 255, 0.05);
  // border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  //padding: 1rem;
  //margin-bottom: 1rem;
  // width: 100%;
  overflow: visible;
  min-height: 350px; //${props => props.chartType === 'pie' ? '550px' : '350px'};
`;

const ChartTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChartDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

interface VegaChartRendererProps {
  title: string;
  description?: string;
  chartType: 'bar' | 'line' | 'pie' | 'scatter' | 'area' | 'histogram' | 'table';
  data: any[];
  xField?: string;
  yField?: string;
  colorField?: string;
  width?: number;
  height?: number;
}

const VegaChartRenderer: React.FC<VegaChartRendererProps> = ({
  title,
  description,
  chartType,
  data,
  xField,
  yField,
  colorField,
  width = 400,
  height = 300
}) => {
  // Transform data for Vega
  const config: ChartConfig = {
    xField,
    yField,
    colorField,
    chartType
  };
  
  const transformedData = transformDataForVega(data, config);
  
  // Extract fields if not provided
  const extractedFields = extractFields(transformedData);
  const finalXField = xField || extractedFields.xField;
  const finalYField = yField || extractedFields.yField;
  const finalColorField = colorField || extractedFields.colorField;
  const generateVegaSpec = () => {
    const baseSpec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      width,
      height,
      data: {
        values: transformedData
      },
      config: {
        background: 'transparent',
        axis: {
          labelColor: 'rgba(255, 255, 255, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.9)',
          gridColor: 'rgba(255, 255, 255, 0.1)',
          tickColor: 'rgba(255, 255, 255, 0.3)'
        },
        legend: {
          labelColor: 'rgba(255, 255, 255, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.9)'
        },
        title: {
          color: 'rgba(255, 255, 255, 0.9)'
        }
      }
    };

    switch (chartType) {
      case 'bar':
        return {
          ...baseSpec,
          mark: 'bar',
          encoding: {
            x: { field: finalXField || 'category', type: 'nominal', title: finalXField || 'Category' },
            y: { field: finalYField || 'value', type: 'quantitative', title: finalYField || 'Value' },
            color: finalColorField ? { field: finalColorField, type: 'nominal' } : undefined,
            tooltip: [
              { field: finalXField || 'category', title: 'Category' },
              { field: finalYField || 'value', title: 'Value' }
            ]
          }
        };

      case 'line':
        return {
          ...baseSpec,
          mark: 'line',
          encoding: {
            x: { field: finalXField || 'x', type: 'temporal', title: finalXField || 'Time' },
            y: { field: finalYField || 'y', type: 'quantitative', title: finalYField || 'Value' },
            color: finalColorField ? { field: finalColorField, type: 'nominal' } : undefined,
            tooltip: [
              { field: finalXField || 'x', title: 'Time' },
              { field: finalYField || 'y', title: 'Value' }
            ]
          }
        };

      case 'pie':
        return {
          ...baseSpec,
          mark: 'arc',
          encoding: {
            theta: { field: finalYField || 'value', type: 'quantitative' },
            color: { 
              field: finalXField || 'category', 
              type: 'nominal',
              legend: {
                orient: 'top-right',
                title: finalXField || 'Category',
                titleColor: 'rgba(255, 255, 255, 0.9)',
                labelColor: 'rgba(255, 255, 255, 0.8)',
                symbolSize: 40,
                titleFontSize: 11,
                labelFontSize: 10,
                offset: 5,
                titleLimit: 400,
                labelLimit: 300,
                columns: 1,
                symbolOpacity: 0.8
              }
            },
            tooltip: [
              { field: finalXField || 'category', title: 'Category' },
              { field: finalYField || 'value', title: 'Value' }
            ]
          },
          // Use autosize to ensure legend fits
          autosize: {
            type: 'fit',
            contains: 'padding',
            resize: true
          },
          // Increase dimensions for pie charts with bottom legend
          width: Math.max(width, 470),
          height: Math.max(height, 300)
        };

      case 'scatter':
        return {
          ...baseSpec,
          mark: 'circle',
          encoding: {
            x: { field: finalXField || 'x', type: 'quantitative', title: finalXField || 'X' },
            y: { field: finalYField || 'y', type: 'quantitative', title: finalYField || 'Y' },
            color: finalColorField ? { field: finalColorField, type: 'nominal' } : undefined,
            size: { value: 60 },
            tooltip: [
              { field: finalXField || 'x', title: 'X' },
              { field: finalYField || 'y', title: 'Y' }
            ]
          }
        };

      case 'area':
        return {
          ...baseSpec,
          mark: 'area',
          encoding: {
            x: { field: finalXField || 'x', type: 'temporal', title: finalXField || 'Time' },
            y: { field: finalYField || 'y', type: 'quantitative', title: finalYField || 'Value' },
            color: finalColorField ? { field: finalColorField, type: 'nominal' } : undefined,
            tooltip: [
              { field: finalXField || 'x', title: 'Time' },
              { field: finalYField || 'y', title: 'Value' }
            ]
          }
        };

      case 'histogram':
        return {
          ...baseSpec,
          mark: 'bar',
          encoding: {
            x: { field: finalXField || 'value', type: 'quantitative', bin: true, title: finalXField || 'Value' },
            y: { aggregate: 'count', type: 'quantitative', title: 'Count' },
            tooltip: [
              { field: finalXField || 'value', title: 'Value Range' },
              { aggregate: 'count', title: 'Count' }
            ]
          }
        };

      case 'table':
        // For tables, we'll return a simple bar chart as fallback
        // Tables should be handled separately in the AnalysisStepRenderer
        return {
          ...baseSpec,
          mark: 'bar',
          encoding: {
            x: { field: finalXField || 'category', type: 'nominal', title: finalXField || 'Category' },
            y: { field: finalYField || 'value', type: 'quantitative', title: finalYField || 'Value' },
            tooltip: [
              { field: finalXField || 'category', title: 'Category' },
              { field: finalYField || 'value', title: 'Value' }
            ]
          }
        };

      default:
        return {
          ...baseSpec,
          mark: 'bar',
          encoding: {
            x: { field: 'category', type: 'nominal' },
            y: { field: 'value', type: 'quantitative' }
          }
        };
    }
  };

  const spec = generateVegaSpec();

  // Show error state if no data
  if (!transformedData || transformedData.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>{title}</ChartTitle>
        {description && <ChartDescription>{description}</ChartDescription>}
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          color: 'rgba(255, 255, 255, 0.6)',
          fontStyle: 'italic'
        }}>
          No data available for visualization
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer chartType={chartType}>
      <ChartTitle>{title}</ChartTitle>
      {description && <ChartDescription>{description}</ChartDescription>}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '300px',
        width: 'max-content'
      }}>
        <Vega spec={spec as any} actions={false} />
      </div>
    </ChartContainer>
  );
};

export default VegaChartRenderer;
